import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import { chromeArg, chromeArgs } from '../validation.ts';
import {
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
  workerValue,
} from './shared.ts';

interface NavigatorWithMemory extends Navigator {
  readonly deviceMemory?: number;
}

const allowedDeviceMemoryGiB = [2, 4, 8, 16, 32] as const;

async function collect(): Promise<RawData> {
  const windowGiB = (navigator as NavigatorWithMemory).deviceMemory ?? null;
  const workerGiB = await workerValue(
    "typeof self.navigator.deviceMemory == 'number' ? self.navigator.deviceMemory : null",
  );
  let requestHeader: unknown = null;
  try {
    const response = await fetch('/network.json', { cache: 'no-store' });
    const body: unknown = await response.json();
    if (isRecord(body) && isRecord(body.headers)) {
      const header =
        body.headers['sec-ch-device-memory'] ?? body.headers['device-memory'];
      requestHeader = header ?? null;
    }
  } catch {
    requestHeader = null;
  }
  return {
    windowGiB,
    workerGiB,
    requestHeader,
  };
}

function validateMemory(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const reasons: string[] = [];
  const value = rawData.windowGiB;
  if (
    value !== null &&
    (typeof value != 'number' ||
      !allowedDeviceMemoryGiB.includes(
        value as (typeof allowedDeviceMemoryGiB)[number],
      ))
  ) {
    reasons.push(
      `${String(value)} GiB is not an allowed Windows 10 x64 Chrome memory bucket; expected 2, 4, 8, 16, or 32 GiB`,
    );
  }
  if (rawData.workerGiB !== value) {
    reasons.push('Worker memory does not match the window');
  }
  if (rawData.requestHeader != null && Number(rawData.requestHeader) != value) {
    reasons.push(
      'Device-Memory request header does not match the JavaScript value',
    );
  }
  const args = chromeArgs(context);
  const configured = args ? chromeArg(args, 'ram') : null;
  if (configured !== null && Number(configured) !== value) {
    reasons.push(`Memory does not match --ram=${configured}`);
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return rawData.windowGiB === null
    ? 'Unavailable'
    : `${String(rawData.windowGiB)} GiB`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateMemory(rawData, context);
}

export const deviceMemoryTest: TestDefinition = {
  id: 'deviceMemory',
  title: 'Device memory',
  category: 'stableProfile',
  spoofingChromeArgs: ['ram'],
  format,
  run,
  validate,
};
